import React, {useState, useEffect, useRef} from 'react';
import cx from 'classnames';
import {useRouter} from 'next/router';
import {useTranslations, createTranslator} from 'next-intl';
import {Grid, Row, Col} from '@/grid';
import withMainLayout from 'layouts/main';
import {SectionPaddingMdToZero} from '@/section';
import Container from '@/container';
import Link from '@/links';
import {withBoldWeight} from '@/hocs/withFontType';
import {H2, P, PMarginTMd} from '@/typography';
import {ListTerms} from '@/ui/lists/list-icons';
import {withMarginBottomMd, withMarginBottomXl, withMarginBottomXxl} from '@/hocs/withSpacing';
import withBreakpoints from '@/hocs/withBreakpoints';
import {withVisibleDesktop993, withHiddenDesktop993} from '@/hocs/withResponsiveUtils';
import Breadcrumbs from '@/ui/breadcrumbs';
import Dropdown from '@/form-fields/dropdown';
import Tabs from '@/ui/tabs/tabs';
import LegalHero from '@/content/legal/hero';
import usePrivacyNoticeTabItems from '@/ui/tabs/tab-items/privacy-notice-tabs';
import legal from 'pages/legal/legal.module.scss';
import {pageMessagesProps} from 'helpers/i18n';
import {
  handlePrint,
  handleDropdown,
  scrollToTopOfPage,
  scrollableDropdownMob
} from '../components/ui/legal/legal.helper';
import shared from '../messages/en/shared.json';
import legalMessages from '../messages/en/legal.json';
import candidatePrivacy from '../messages/en/candidatePrivacy.json';

const seoMetas = props => {
  const seoT = createTranslator({locale: props.locale, messages: props.messages});
  return {
    title: seoT('candidatePrivacy.seo.title'),
    description: seoT('candidatePrivacy.seo.description'),
    canonical: seoT('candidatePrivacy.seo.canonical')
  };
};

const List = props => <ListTerms {...props} />;
const BoldSpan = withBoldWeight('span');
const MarginedH2 = withMarginBottomMd(H2);
const DivMarginedBottomXl = withMarginBottomXl('div');
const BreadcrumbsDesktop = withVisibleDesktop993(withMarginBottomXxl('div'));
const HiddenInDesktop = withHiddenDesktop993('div');

const CandidatePrivacyPolicyPage = () => {
  const router = useRouter();
  const dropdownRef = useRef();
  const [isDropdownFixed, setIsDropdownFixed] = useState(false);
  const t = useTranslations('candidatePrivacy');
  const legalT = useTranslations('legal');
  const tabItems = usePrivacyNoticeTabItems();

  const tableOfContents = t.raw('tableOfContents');

  const CandidatePrivacyPolicyContent = () => (
    <>
      <MarginedH2>{t('heading')}</MarginedH2>
      <DivMarginedBottomXl>
        <Link onClick={handlePrint} variant="secondary-no-gradient-download" className="no-print">
          {t('downloadLabel')}
        </Link>
      </DivMarginedBottomXl>
      <div className={cx(legal['table-of-contents'], 'no-print')}>
        <ol>
          {tableOfContents.map((item, index) => (
            <li key={index}>
              <Link href={item.urlCopy} variant="green800">
                {item.title}
              </Link>
            </li>
          ))}
        </ol>
      </div>
      <P>{t('intro.paragraph1')}</P>
      <P>{t('intro.paragraph2')}</P>
      <P>
        {t.rich('intro.paragraph3Rich', {bold: chunks => <BoldSpan>{chunks}</BoldSpan>})}
      </P>
      <P>
        {t.rich('intro.paragraph4Rich', {
          a: chunks => (
            <Link variant="default" href="/privacy">
              {chunks}
            </Link>
          )
        })}
      </P>
      <P>{t('intro.paragraph5')}</P>

      <H2 className={cx(legal['scroll-mg-t'], 'heading-4')} id="data-controllers">
        {t('sections.dataControllers.title')}
      </H2>
      {t.raw('sections.dataControllers.paragraphs').map(paragraph => (
        <P key={paragraph}>{paragraph}</P>
      ))}

      <H2 className={cx(legal['scroll-mg-t'], 'heading-4')} id="personal-information">
        {t('sections.personalInformation.title')}
      </H2>
      <P className="heading-5">
        <BoldSpan>{t('sections.personalInformation.collectFromYou.label')}</BoldSpan>
      </P>
      <PMarginTMd>{t('sections.personalInformation.collectFromYou.intro')}</PMarginTMd>
      <List>
        {t.raw('sections.personalInformation.collectFromYou.items').map(item => (
          <li key={item}>{item}</li>
        ))}
      </List>
      <P className="heading-5">
        <BoldSpan>{t('sections.personalInformation.collectFromOtherSources.label')}</BoldSpan>
      </P>
      {t.raw('sections.personalInformation.collectFromOtherSources.paragraphs').map(
        (paragraph, index) => {
          const ParagraphTag = index === 0 ? PMarginTMd : P;
          return <ParagraphTag key={paragraph}>{paragraph}</ParagraphTag>;
        }
      )}

      <H2 className={cx(legal['scroll-mg-t'], 'heading-4')} id="uses-of-information">
        {t('sections.usesOfInformation.title')}
      </H2>
      <P className="heading-5">
        <BoldSpan>{t('sections.usesOfInformation.lawfulBasis.label')}</BoldSpan>
      </P>
      {t.raw('sections.usesOfInformation.lawfulBasis.paragraphs').map(paragraph => (
        <P key={paragraph}>{paragraph}</P>
      ))}

      <P className="heading-5">
        <BoldSpan>{t('sections.usesOfInformation.purposes.label')}</BoldSpan>
      </P>
      <P>{t('sections.usesOfInformation.purposes.intro')}</P>
      <List>
        {t.raw('sections.usesOfInformation.purposes.items').map(item => (
          <li key={item}>{item}</li>
        ))}
      </List>

      <P className="heading-5">
        <BoldSpan>{t('sections.usesOfInformation.automatedDecision.label')}</BoldSpan>
      </P>
      <P>{t('sections.usesOfInformation.automatedDecision.paragraph')}</P>

      <P className="heading-5">
        <BoldSpan>{t('sections.usesOfInformation.aggregateData.label')}</BoldSpan>
      </P>
      <P>{t('sections.usesOfInformation.aggregateData.paragraph')}</P>

      <H2 className={cx(legal['scroll-mg-t'], 'heading-4')} id="disclosure-of-information">
        {t('sections.disclosureOfInformation.title')}
      </H2>
      {t.raw('sections.disclosureOfInformation.paragraphs').map(paragraph => (
        <P key={paragraph}>{paragraph}</P>
      ))}

      <H2 className={cx(legal['scroll-mg-t'], 'heading-4')} id="how-we-store-data">
        {t('sections.howWeStoreData.title')}
      </H2>
      <P className="heading-5">
        <BoldSpan>{t('sections.howWeStoreData.security.label')}</BoldSpan>
      </P>
      {t.raw('sections.howWeStoreData.security.paragraphs').map(paragraph => (
        <P key={paragraph}>{paragraph}</P>
      ))}

      <P className="heading-5">
        <BoldSpan>{t('sections.howWeStoreData.whereWeStore.label')}</BoldSpan>
      </P>
      {t.raw('sections.howWeStoreData.whereWeStore.paragraphs').map(paragraph => (
        <P key={paragraph}>{paragraph}</P>
      ))}

      <P className="heading-5">
        <BoldSpan>{t('sections.howWeStoreData.howLong.label')}</BoldSpan>
      </P>
      {t.raw('sections.howWeStoreData.howLong.paragraphs').map(paragraph => (
        <P key={paragraph}>{paragraph}</P>
      ))}

      <H2 className={cx(legal['scroll-mg-t'], 'heading-4')} id="your-rights">
        {t('sections.yourRights.title')}
      </H2>
      <P>{t('sections.yourRights.intro')}</P>
      <List>
        {t.raw('sections.yourRights.items').map(item => (
          <li key={item}>{item}</li>
        ))}
      </List>
      <P>
        {t.rich('sections.yourRights.icoParagraphRich', {
          a: chunks => (
            <Link
              variant="default"
              href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/individual-rights/individual-rights/"
              target="_blank">
              {chunks}
            </Link>
          )
        })}
      </P>
      <P>{t('sections.yourRights.exerciseIntro')}</P>
      <List>
        {t.raw('sections.yourRights.exerciseItems').map(item => (
          <li key={item}>{item}</li>
        ))}
      </List>
      <P>{t('sections.yourRights.outro')}</P>

      <H2 className={cx(legal['scroll-mg-t'], 'heading-4')} id="how-to-complain">
        {t('sections.howToComplain.title')}
      </H2>
      <P>
        {t.rich('sections.howToComplain.gdprComplaintRich', {
          br: () => <br />,
          a: chunks => (
            <Link
              variant="default"
              href="https://eur-lex.europa.eu/legal-content/EN/TXT/PDF/?uri=CELEX:32016R0679&from=EN"
              target="_blank">
              {chunks}
            </Link>
          )
        })}
      </P>
      <P>
        {t.rich('sections.howToComplain.icoContactRich', {
          a: chunks => (
            <Link variant="default" href="https://ico.org.uk/concerns/" target="_blank">
              {chunks}
            </Link>
          )
        })}
      </P>
      <P>{t('sections.howToComplain.greeceParagraph')}</P>

      <H2 className={cx(legal['scroll-mg-t'], 'heading-4')} id="contact">
        {t('sections.contact.title')}
      </H2>
      {t.raw('sections.contact.paragraphs').map(paragraph => (
        <P key={paragraph}>{paragraph}</P>
      ))}
    </>
  );

  const ResponsiveMenu = withBreakpoints({
    desktop: () => (
      <Tabs
        activeTab="candidates-policy"
        title={legalT('sectionTitles.privacyNotice')}
        tabItems={tabItems}
        router={router}
      />
    ),
    mobile: () => (
      <div
        ref={dropdownRef}
        className={cx(
          isDropdownFixed ? legal['legal-dropdown-fixed'] : legal['legal-dropdown'],
          'no-print'
        )}>
        <Dropdown
          name="privacy-notice"
          defaultValue="candidates-policy"
          variant="green800"
          values={tabItems}
          onChange={item => handleDropdown(item, router)}
        />
      </div>
    )
  });

  useEffect(() => {
    scrollableDropdownMob(dropdownRef, setIsDropdownFixed);
  }, []);

  return (
    <>
      <LegalHero title={legalT('sectionTitles.privacyNotice')} />
      <SectionPaddingMdToZero>
        <Container>
          <Grid fluid>
            <BreadcrumbsDesktop>
              <Breadcrumbs
                clickableLink={{text: legalT('breadcrumb.legal'), urlCopy: '/legal'}}
                title={legalT('sectionTitles.privacyNotice')}
                activeTab={tabItems.find(item => item.value === 'candidates-policy').text}
              />
            </BreadcrumbsDesktop>
            <Row>
              <Col xs={12} md={12} lg={3}>
                <ResponsiveMenu />
              </Col>
              <Col xs={12} md={12} lgOffset={1} lg={8}>
                <CandidatePrivacyPolicyContent />
              </Col>
            </Row>
          </Grid>
          <HiddenInDesktop>
            <Link variant="go-to-top-arrow" onClick={scrollToTopOfPage} />
          </HiddenInDesktop>
        </Container>
      </SectionPaddingMdToZero>
    </>
  );
};

export default withMainLayout(CandidatePrivacyPolicyPage, seoMetas);

export const messagesForIntl = {shared, legal: legalMessages, candidatePrivacy};

export function getStaticProps() {
  return pageMessagesProps(messagesForIntl);
}
